import type { ArticleInput } from "./content";

/** Long-form article data for the nine core service routes, keyed by route path. */
export const serviceArticles: Record<string, ArticleInput> = {
  "/garage-door-repair": {
    title: "Garage Door Repair",
    topic: "general garage door repair",
    failure:
      "A garage door is a balanced mechanical system, and when one component wears out the rest of the system pays for it.",
    fix: "The failed component is replaced with a correctly sized part, and every adjacent part it stressed — rollers, bearings, hinges, brackets — gets inspected in the same visit.",
    causes: [
      "Springs at the end of their cycle life",
      "Loose lag bolts and jamb hardware from vibration",
      "Dry or seized roller and bearing races",
      "Track knocked out of plumb by an impact",
      "Opener limits and force settings drifting out of range",
      "Corrosion from road salt and humidity",
    ],
    symptoms: [
      "Door rises a few inches and stops",
      "Loud bang followed by a dead door",
      "One side of the door sits higher than the other",
      "Grinding, scraping or popping while moving",
      "Opener strains, hums or reverses immediately",
    ],
  },
  "/garage-door-spring-repair": {
    title: "Garage Door Spring Repair",
    topic: "spring replacement",
    failure:
      "Springs carry the entire weight of the door, and once one snaps the opener is pulling a load it was never designed to lift.",
    fix: "Torsion or extension springs are replaced in matched pairs with the correct wire size, length and cycle rating, bearings are serviced while the shaft is accessible, and the door is re-balanced by hand.",
    causes: [
      "Normal cycle-life exhaustion (most springs are rated 10,000 cycles)",
      "Undersized springs installed for the door's real weight",
      "Rust in the coils from a damp or unheated garage",
      "A single spring on a heavy double door",
      "Cold snaps that finish off already-fatigued steel",
    ],
    symptoms: [
      "A visible two-inch gap in the coil above the door",
      "Door feels extremely heavy to lift by hand",
      "Opener runs but the door barely moves",
      "Cables hanging loose off the drums",
      "Door slams down fast when released",
    ],
    parts: [
      "Torsion springs in common wire sizes and lengths",
      "Extension springs with safety cables",
      "Center bearings, end bearings and bearing plates",
      "Winding cones, torsion shafts and couplers",
      "Lift cables and cable drums",
    ],
  },
  "/garage-door-opener-repair": {
    title: "Garage Door Opener Repair",
    topic: "opener repair",
    failure:
      "Opener trouble is almost always one of four things: power, the safety sensor circuit, the drive system, or the logic board.",
    fix: "Each is tested in order, the failed element is repaired or replaced, then limits, force and remote pairing are re-set.",
    causes: [
      "Photo eyes knocked out of alignment or wires pinched behind brackets",
      "Stripped drive gears from lifting an unbalanced door",
      "Failed capacitors or logic boards on older units",
      "Travel and force limits drifting out of range",
      "Surge damage after a storm",
    ],
    symptoms: [
      "Motor runs but the door does not move",
      "Door closes then immediately reverses",
      "Blinking LED on the motor head",
      "Remote works only from a few feet away",
      "Wall control does nothing while the remote works",
    ],
  },
  "/garage-door-cable-repair": {
    title: "Garage Door Cable Repair",
    topic: "lift cable and drum replacement",
    failure:
      "Lift cables translate spring tension into door movement, so when one lets go the door drops on one side and wedges itself in the tracks.",
    fix: "Cables are replaced in matched pairs, drums and set screws are inspected, and the door is re-seated square in the opening.",
    causes: [
      "Fraying where the cable wraps the drum",
      "Rust at the bottom bracket from a wet slab",
      "A cable that jumped the drum groove after a spring break",
      "Over-wound or crossed cables from an earlier bad repair",
      "Bottom bracket or drum set screws working loose",
    ],
    symptoms: [
      "Cable coiled loose on the floor",
      "Door hanging at an angle and binding in the track",
      "Frayed strands or rust along the cable",
      "Door drops quickly when released",
      "Grinding at the drum during travel",
    ],
  },
  "/garage-door-track-repair": {
    title: "Garage Door Track Repair",
    topic: "track straightening and alignment",
    failure:
      "A track that is out of plumb by a fraction of an inch chews through rollers and eventually lets the door derail.",
    fix: "Vertical and horizontal tracks are straightened, re-plumbed and re-hung, and dented sections that cannot be trued are replaced.",
    causes: [
      "A bumper tap or ladder strike on the vertical track",
      "Loose lag bolts letting the track spread under load",
      "Sagging horizontal track hangers",
      "Debris and hardened grease in the track channel",
      "Rust pitting on the running surface",
    ],
    symptoms: [
      "Door catches at the same spot every time",
      "Visible gap between roller and track",
      "Rollers popping out of the track edge",
      "Scraping metal near the curve",
      "Door shifts sideways as it travels",
    ],
  },
  "/garage-door-roller-replacement": {
    title: "Garage Door Roller Replacement",
    topic: "roller, hinge and bearing replacement",
    failure:
      "Rollers and hinges are consumable parts, and flat spots, seized bearings and cracked hinge plates make the door rattle, drag and pull sideways.",
    fix: "Worn rollers are replaced with sealed nylon rollers, hinges and brackets are swapped by number and position, and the whole door is lubricated and torqued.",
    causes: [
      "Unsealed steel rollers with dry, contaminated bearings",
      "Flat spots from a door parked on a bad roller for years",
      "Cracked hinge plates from a door running out of balance",
      "Salt and moisture seizing the bearing races",
      "Bent stems from a previous off-track event",
    ],
    symptoms: [
      "Door loud enough to hear inside the house",
      "Rollers wobble or no longer spin",
      "Door shudders as it travels",
      "Cracked or bent hinges between panels",
      "Black dust on the track from metal wear",
    ],
  },
  "/garage-door-off-track-repair": {
    title: "Off-Track Garage Door Repair",
    topic: "off-track recovery",
    failure:
      "A door off its track is under uncontrolled load, and every extra attempt to run it bends more hardware.",
    fix: "The door is secured, tension is released safely, every roller is re-seated, and the broken cable, bent track or failed bracket that caused it is repaired in the same visit.",
    causes: [
      "A snapped lift cable dropping one side of the door",
      "Backing into the door or hitting the track",
      "Rollers worn past the point of holding the channel",
      "An obstruction left in the track path",
      "A broken spring letting the door fall",
    ],
    symptoms: [
      "Door leaning out of the opening",
      "Rollers sitting outside the track",
      "Door stuck part-way and will not budge",
      "Visible kink or twist in the track",
      "Panel damage where the door jammed",
    ],
  },
  "/garage-door-installation": {
    title: "Garage Door Installation",
    topic: "new door installation",
    failure:
      "A door with rusted-through sections, repeated panel damage or a failed frame is money better spent on replacement than on another repair.",
    fix: "The old door and hardware come out, and a new door goes in with new track, hinges, rollers and springs sized to the finished door weight.",
    causes: [
      "Rust or rot through the bottom section",
      "Multiple damaged or mismatched panels",
      "A discontinued door line with no replacement sections",
      "An uninsulated door on an attached garage",
      "Storm or vehicle damage beyond panel repair",
    ],
    symptoms: [
      "Repeated repairs on the same door",
      "Sections separating at the hinges",
      "Daylight and water getting in around the door",
      "Door too heavy for its opener even when balanced",
      "Cosmetic damage that will not straighten",
    ],
    steps: [
      "Measure the opening, headroom, backroom and existing track",
      "Confirm door style, insulation value and spring specification",
      "Remove and haul away the old door and hardware",
      "Install new sections, track, hinges, rollers and springs",
      "Install or re-hang the opener and program controls",
      "Balance the door and test the safety reverse",
    ],
  },
  "/emergency-garage-door-repair": {
    title: "Emergency Garage Door Repair",
    topic: "emergency garage door service",
    failure:
      "Springs break at midnight and cables snap on the way to work, and a door stuck open leaves the house exposed until it is fixed.",
    fix: "A technician is dispatched with the parts that fail most often on board, and the call ends with a working door or a secured opening rather than a scheduled follow-up.",
    causes: [
      "Broken torsion springs during a cold snap",
      "Snapped lift cables on a heavily used door",
      "Doors derailed after an impact",
      "Failed openers and dead safety sensors",
      "Storm damage to sections and tracks",
    ],
    symptoms: [
      "Door will not close and the house is open",
      "Loud bang and the door stopped working",
      "Door slammed down and will not lift",
      "Vehicle trapped inside the garage",
      "Door hanging crooked in the opening",
    ],
  },
};

/** Additional service / common-problem pages, each with its own URL and article. */
export type ExtraPage = ArticleInput & {
  slug: string;
  nav: string;
  short: string;
  blurb: string;
};

export const extraPages: ExtraPage[] = [
  {
    slug: "broken-garage-door-spring",
    nav: "Broken Spring",
    title: "Broken Garage Door Spring Repair",
    short: "Snapped torsion and extension springs replaced in matched pairs.",
    blurb: "The loud bang you heard was a spring. The door will not lift until it is replaced.",
    topic: "broken spring replacement",
    failure:
      "A snapped spring leaves the full weight of the door — often 150 to 400 pounds — resting on the opener and the cables.",
    fix: "The broken spring and its partner are replaced together with correctly rated steel, the bearings are serviced, and the door is re-balanced.",
    causes: [
      "Cycle-life exhaustion after years of daily use",
      "Undersized springs for the door's real weight",
      "Rust in the coils",
      "Cold weather finishing off fatigued steel",
    ],
    symptoms: [
      "A two-inch gap in the spring coil",
      "Door will not lift, or lifts a few inches and stops",
      "Opener hums and strains without moving the door",
      "Door feels impossibly heavy by hand",
    ],
  },
  {
    slug: "garage-door-opener-replacement",
    nav: "Opener Replacement",
    title: "Garage Door Opener Replacement",
    short: "New chain, belt and wall-mount openers installed and programmed.",
    blurb: "When a motor or board has failed on an older unit, replacement is the better spend.",
    topic: "opener replacement",
    failure:
      "Older openers reach a point where the board, motor or gear set costs as much as a new unit with a warranty behind it.",
    fix: "A new opener is installed on properly balanced hardware, limits and force are set, remotes and keypads are paired, and the safety reverse is tested.",
    causes: [
      "Failed logic board or capacitor on a 15-year-old unit",
      "Stripped gear sets from lifting an unbalanced door",
      "No rolling-code security on pre-1993 units",
      "Motor burnout after running against a broken spring",
    ],
    symptoms: [
      "Opener trips the breaker or does nothing at all",
      "Grinding from the motor head with no movement",
      "Parts no longer available for the model",
      "Door operation is erratic even after adjustment",
    ],
  },
  {
    slug: "garage-door-sensor-repair",
    nav: "Sensor Repair",
    title: "Garage Door Sensor Repair",
    short: "Safety photo eyes aligned, rewired or replaced.",
    blurb: "A door that reverses right before it closes is almost always a sensor problem.",
    topic: "safety sensor repair",
    failure:
      "The photo-eye circuit is a federally required safety system, and the opener refuses to close the door whenever that beam is broken or the wiring is suspect.",
    fix: "The eyes are cleaned, re-aimed and re-bracketed, damaged wiring is replaced, and the close cycle is verified with an obstruction test.",
    causes: [
      "Brackets bumped by a car door, bike or trash can",
      "Sun glare washing out the receiver",
      "Pinched or corroded low-voltage wire",
      "Dirty or fogged lenses",
      "Failed sensor pair after a surge",
    ],
    symptoms: [
      "Door starts down then reverses to full open",
      "Opener lights blink a set number of times",
      "One sensor LED off or flickering",
      "Door closes only when the wall button is held",
    ],
  },
  {
    slug: "garage-door-panel-replacement",
    nav: "Panel Replacement",
    title: "Garage Door Panel Replacement",
    short: "Damaged sections replaced without replacing the whole door.",
    blurb: "Dented or cracked sections swapped where the door line is still available.",
    topic: "panel and section replacement",
    failure:
      "A dented or cracked section weakens the whole door, and a bent section pulls the rollers out of line as it travels.",
    fix: "The damaged section is removed and replaced with a matching panel, and the hinges, rollers and struts around it are inspected and re-torqued.",
    causes: [
      "Backing a vehicle into the door",
      "Storm and wind damage",
      "Rust through the bottom section",
      "Impact from equipment stored against the door",
    ],
    symptoms: [
      "Visible dent, crack or split in one section",
      "Door binds where the damaged panel travels",
      "Gaps or daylight at the damaged joint",
      "Hinges pulling out of the panel skin",
    ],
  },
  {
    slug: "garage-door-weather-seal-replacement",
    nav: "Weather Seals",
    title: "Garage Door Weather Seal Replacement",
    short: "Bottom seals, jamb stops and thresholds replaced.",
    blurb: "Stop water, leaves, cold air and pests from getting in under the door.",
    topic: "weather seal replacement",
    failure:
      "Rubber seals harden, crack and shrink, and once the bottom astragal fails water and cold air move straight into the garage.",
    fix: "The old bottom seal, jamb stops and threshold are removed and replaced with correctly profiled material, and the floor line is checked for level.",
    causes: [
      "UV and ozone hardening the rubber",
      "Salt and freeze-thaw cracking the astragal",
      "An uneven slab leaving a permanent gap",
      "Rodents chewing through the seal",
    ],
    symptoms: [
      "Daylight visible under a closed door",
      "Water running into the garage in heavy rain",
      "Leaves and grit blowing in",
      "Noticeably cold garage in winter",
    ],
  },
  {
    slug: "garage-door-tune-up",
    nav: "Tune-Up",
    title: "Garage Door Tune-Up & Maintenance",
    short: "Annual balance, lubrication, hardware and safety service.",
    blurb: "Fifteen minutes of yearly attention removes most emergency calls.",
    topic: "preventative maintenance",
    failure:
      "Every part of an overhead door is on a wear clock, and untouched hardware fails at the worst possible moment.",
    fix: "Balance and spring tension are checked, cables and drums inspected, rollers and bearings lubricated, hardware torqued, sensors cleaned and the safety reverse tested.",
    causes: [
      "Dry bearings and hinges",
      "Loose lag bolts and bracket fasteners",
      "Springs losing tension over time",
      "Dirty photo eyes and drifting limits",
    ],
    symptoms: [
      "Door has gotten louder over the past year",
      "Door feels heavier by hand than it used to",
      "Slight jerk or hesitation during travel",
      "No service performed in more than a year",
    ],
    steps: [
      "Hand balance and spring tension test",
      "Cable, drum and bottom bracket inspection",
      "Roller, hinge and bearing lubrication",
      "Full hardware torque check",
      "Photo eye cleaning and alignment",
      "Limit, force and safety reverse testing",
    ],
  },
  {
    slug: "garage-door-wont-open",
    nav: "Won't Open",
    title: "Garage Door Won't Open",
    short: "Dead openers, snapped springs and jammed doors.",
    blurb: "The three usual culprits: a broken spring, a snapped cable or a failed opener.",
    topic: "a door that will not open",
    failure:
      "A door that will not open is either mechanically blocked or has lost the spring assistance that makes it liftable.",
    fix: "We identify which of the three systems failed — springs, cables or opener — and repair that system rather than forcing the door.",
    causes: [
      "Broken torsion or extension spring",
      "Snapped or jumped lift cable",
      "Opener gear, board or capacitor failure",
      "Door locked or bound in a bent track",
      "Power loss to the motor head",
    ],
    symptoms: [
      "Remote and wall button do nothing",
      "Motor runs but nothing moves",
      "Door lifts an inch and stops",
      "Door is far too heavy to lift by hand",
    ],
  },
  {
    slug: "garage-door-wont-close",
    nav: "Won't Close",
    title: "Garage Door Won't Close",
    short: "Reversing doors, sensor faults and limit problems.",
    blurb: "A door that reverses on its own is a safety circuit telling you something.",
    topic: "a door that will not close",
    failure:
      "The opener will not complete a close cycle when the safety sensors are blocked or misaligned, or when the close limit is set past the floor.",
    fix: "The sensor circuit and wiring are tested and re-aimed, the close limit and force are reset, and any track obstruction or binding roller is corrected.",
    causes: [
      "Misaligned or dirty photo eyes",
      "Damaged low-voltage sensor wiring",
      "Close limit set beyond the floor line",
      "Obstruction or debris in the track",
      "Binding roller stopping travel early",
    ],
    symptoms: [
      "Door reverses right before touching the floor",
      "Opener lights flash a repeating pattern",
      "Door closes only while holding the wall button",
      "Door stops a few inches from the ground",
    ],
  },
  {
    slug: "garage-door-making-noise",
    nav: "Noisy Door",
    title: "Noisy Garage Door Repair",
    short: "Grinding, rattling, popping and squealing diagnosed and fixed.",
    blurb: "Noise is a diagnosis. Each sound points to a different failing part.",
    topic: "noise diagnosis and repair",
    failure:
      "A garage door gets loud when metal runs on metal — dry bearings, flat-spotted rollers, cracked hinges or loose hardware.",
    fix: "Each noise source is isolated, worn rollers and hinges are replaced with sealed nylon parts, bearings are serviced and every fastener is torqued.",
    causes: [
      "Unsealed steel rollers with dry bearings",
      "Seized center or end bearings",
      "Cracked hinges between sections",
      "Loose lag bolts and bracket hardware",
      "Chain or belt tension out of spec",
    ],
    symptoms: [
      "Grinding or squealing during travel",
      "Rhythmic popping at the same point",
      "Rattling loud enough to hear upstairs",
      "Bang at the top or bottom of travel",
    ],
  },
  {
    slug: "garage-door-stuck-halfway",
    nav: "Stuck Halfway",
    title: "Garage Door Stuck Halfway",
    short: "Doors jammed mid-travel, secured and recovered.",
    blurb: "A door stopped mid-opening is holding load. Stop cycling it and call.",
    topic: "a door jammed mid-travel",
    failure:
      "A door that stops part-way is usually blocked by a failed roller, a bent track or a cable that jumped its drum.",
    fix: "The door is secured, the obstruction or failed part is identified, and the roller, track, cable or drum responsible is repaired before the door is cycled again.",
    causes: [
      "Roller collapsed or off the track",
      "Kink or dent in the vertical track",
      "Cable off its drum groove",
      "Obstruction wedged in the track channel",
      "Opener force limit stopping travel early",
    ],
    symptoms: [
      "Door stops at the same height every time",
      "Loud catch or thud at the jam point",
      "Door hangs crooked while stuck",
      "Opener reverses when it hits the spot",
    ],
  },
  {
    slug: "garage-door-opens-unevenly",
    nav: "Uneven Door",
    title: "Garage Door Opens Unevenly",
    short: "Crooked doors re-squared, cables and drums corrected.",
    blurb: "One side higher than the other is a cable, drum or balance fault.",
    topic: "uneven and crooked door repair",
    failure:
      "When one lift cable is longer, looser or off its drum, that side of the door rises at a different rate and the door binds in the tracks.",
    fix: "Cables are replaced in pairs, drums are re-seated and set to matching tension on the shaft, and the door is re-squared and re-balanced.",
    causes: [
      "Stretched or slipped lift cable",
      "Drum set screws working loose on the shaft",
      "One broken spring on a two-spring door",
      "Bent bottom bracket on one side",
      "Track out of plumb on one jamb",
    ],
    symptoms: [
      "Visible tilt in the closed or open position",
      "Grinding on one side only",
      "Loose cable on one drum",
      "Door binds harder as it rises",
    ],
  },
  {
    slug: "garage-door-remote-not-working",
    nav: "Remote Problems",
    title: "Garage Door Remote Not Working",
    short: "Remotes, keypads, wall controls and receivers.",
    blurb: "Reprogramming, receiver testing and replacement for dead controls.",
    topic: "remote and keypad service",
    failure:
      "Remote faults come down to the transmitter, the receiver in the motor head, or interference between them.",
    fix: "The transmitter is tested and re-paired, the receiver and antenna are checked, and interference sources or failed boards are addressed.",
    causes: [
      "Dead transmitter battery or worn contacts",
      "Lost programming after a power event",
      "Damaged or shortened receiver antenna",
      "LED bulb or nearby electronics causing interference",
      "Failed receiver board",
    ],
    symptoms: [
      "Remote works only within a few feet",
      "One remote works and another does not",
      "Keypad accepts the code but nothing happens",
      "Wall control works, remotes do not",
    ],
  },
  {
    slug: "garage-door-replacement",
    nav: "Door Replacement",
    title: "Garage Door Replacement",
    short: "Full door replacement with new hardware and springs.",
    blurb: "When repair no longer makes sense, a full replacement done properly.",
    topic: "full door replacement",
    failure:
      "Rusted sections, repeated panel damage and discontinued door lines eventually put a door past the point where repair is the right spend.",
    fix: "The old door and hardware are removed and hauled away, and a new insulated or non-insulated door goes in with new track, hinges, rollers and correctly sized springs.",
    causes: [
      "Rust through the bottom sections",
      "Multiple damaged or mismatched panels",
      "Discontinued sections with no match available",
      "Structural damage after an impact",
    ],
    symptoms: [
      "Repeated repairs in new places each year",
      "Sections separating at the hinge line",
      "Door will not seal against the floor anywhere",
      "Cosmetic damage that cannot be straightened",
    ],
  },
];

export function findExtraPage(slug: string) {
  return extraPages.find((p) => p.slug === slug);
}
